import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  login as cognitoLogin,
  completeNewPassword,
  forgotPassword,
  confirmForgotPassword,
  associateSoftwareToken,
  verifySoftwareToken,
  respondToMfaChallenge,
  type LoginResult,
} from "@/lib/cognito";

export default function LoginPage() {
  const { isAuthenticated, loginWithTokens } = useAuth();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password change state
  const [challenge, setChallenge] = useState<{
    session: string;
    email: string;
  } | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Forgot password state
  const [forgotStep, setForgotStep] = useState<"none" | "email" | "code">("none");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  // MFA state
  const [mfaSetup, setMfaSetup] = useState<{
    session: string;
    email: string;
    secretCode: string;
  } | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<{
    session: string;
    email: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/app", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setMounted(true);
  }, []);

  async function handleLoginResult(result: LoginResult) {
    switch (result.type) {
      case "authenticated":
        loginWithTokens(result.idToken, result.accessToken, result.refreshToken, result.expiresIn);
        break;
      case "new_password_required":
        setChallenge({ session: result.session, email: result.email });
        setPassword("");
        break;
      case "mfa_setup_required": {
        setIsSubmitting(false);
        try {
          const { secretCode, session } = await associateSoftwareToken(result.session);
          setMfaSetup({ session, email: result.email, secretCode });
        } catch (err) {
          console.error("AssociateSoftwareToken failed:", err);
          setError("Failed to set up two-factor authentication. Please sign in again.");
          setChallenge(null);
        }
        break;
      }
      case "software_token_mfa":
        setMfaChallenge({ session: result.session, email: result.email });
        setMfaCode("");
        break;
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result: LoginResult = await cognitoLogin(email.trim(), password);
      await handleLoginResult(result);
    } catch (err) {
      const code = (err as Record<string, unknown>).code as string;
      if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
        setError("Incorrect email or password");
      } else if (code === "UserNotConfirmedException") {
        setError("Account not verified. Please check your email for a confirmation link.");
      } else if (code === "PasswordResetRequiredException") {
        setError("Password reset required. Use 'Forgot password?' to reset.");
      } else if (code === "TooManyRequestsException") {
        setError("Too many attempts. Please wait a moment and try again.");
      } else {
        setError((err as Error).message || "Login failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 12) {
      setError("Password must be at least 12 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await completeNewPassword(
        challenge!.email,
        newPassword,
        challenge!.session,
      );
      // Password changed — clear challenge form before handling next step
      // so the UI doesn't stay stuck on the password screen
      setChallenge(null);
      setIsSubmitting(false);
      await handleLoginResult(result);
    } catch (err) {
      const code = (err as Record<string, unknown>).code as string;
      if (code === "InvalidPasswordException") {
        setError("Password must contain uppercase, lowercase, numbers, and symbols");
      } else if (code === "NotAuthorizedException") {
        setError("Session expired. Please sign in again.");
        setChallenge(null);
      } else if (code === "TooManyRequestsException") {
        setError("Too many attempts. Please wait a moment and try again.");
      } else {
        setError((err as Error).message || "Failed to set password");
      }
      setIsSubmitting(false);
    }
  }

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await forgotPassword(forgotEmail.trim());
      setForgotStep("code");
    } catch (err) {
      const code = (err as Record<string, unknown>).code as string;
      if (code === "UserNotFoundException") {
        // Don't reveal whether user exists — show same message
        setForgotStep("code");
      } else if (code === "LimitExceededException" || code === "TooManyRequestsException") {
        setError("Too many attempts. Please try again later.");
      } else if (code === "InvalidParameterException") {
        setError("Please enter a valid email address.");
      } else {
        setError((err as Error).message || "Failed to send reset code");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (resetPassword !== resetConfirm) {
      setError("Passwords do not match");
      return;
    }
    if (resetPassword.length < 12) {
      setError("Password must be at least 12 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmForgotPassword(forgotEmail.trim(), resetCode.trim(), resetPassword);
      setResetSuccess(true);
    } catch (err) {
      const code = (err as Record<string, unknown>).code as string;
      if (code === "CodeMismatchException") {
        setError("Invalid verification code");
      } else if (code === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new one.");
      } else if (code === "InvalidPasswordException") {
        setError("Password must contain uppercase, lowercase, numbers, and symbols");
      } else if (code === "LimitExceededException" || code === "TooManyRequestsException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError((err as Error).message || "Failed to reset password");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Password change screen
  if (challenge) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.05] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                    <KeyIcon className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </div>

              <div className="mb-8 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  Set your password
                </h1>
                <p className="text-[13px] text-slate-400">
                  Choose a secure password for your account
                </p>
              </div>

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    New Password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                <div className="rounded-lg border border-slate-700/30 bg-slate-800/20 px-4 py-3">
                  <p className="text-[12px] leading-relaxed text-slate-500">
                    Min 12 characters with uppercase, lowercase, numbers, and symbols.
                  </p>
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                    <p className="text-[13px] text-red-400">{error}</p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Setting password...
                      </span>
                    ) : (
                      "Set Password & Continue"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password — enter email
  if (forgotStep === "email") {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.06] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                    <KeyIcon className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </div>

              <div className="mb-8 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  Reset Password
                </h1>
                <p className="text-[13px] text-slate-400">
                  Enter your email to receive a verification code
                </p>
              </div>

              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Email
                  </Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                    <p className="text-[13px] text-red-400">{error}</p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || !forgotEmail.trim()}
                    className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Sending code...
                      </span>
                    ) : (
                      "Send Verification Code"
                    )}
                  </Button>
                </div>

                <p className="text-center text-[13px] text-slate-400">
                  <button
                    type="button"
                    onClick={() => { setForgotStep("none"); setResetCode(""); setResetPassword(""); setResetConfirm(""); setError(null); }}
                    className="font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    Back to login
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password — enter code + new password
  if (forgotStep === "code") {
    if (resetSuccess) {
      return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
          <GridBg />
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-500/[0.05] blur-[120px]" />

          <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <div className="rounded-2xl bg-gradient-to-b from-green-500/20 via-green-500/5 to-transparent p-px">
              <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
                <div className="mb-6 flex justify-center">
                  <div className="relative">
                    <div className="absolute -inset-3 rounded-full bg-green-500/10 blur-md" />
                    <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-green-500/20 bg-green-500/[0.08]">
                      <ShieldIcon className="h-8 w-8 text-green-400" />
                    </div>
                  </div>
                </div>

                <div className="mb-8 text-center">
                  <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                    Password Reset
                  </h1>
                  <p className="text-[13px] text-slate-400">
                    Your password has been changed successfully
                  </p>
                </div>

                <Button
                  size="lg"
                  onClick={() => { setForgotStep("none"); setResetSuccess(false); setForgotEmail(""); setResetCode(""); setResetPassword(""); setResetConfirm(""); setError(null); }}
                  className="w-full rounded-xl border-0 bg-green-600 py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-green-500/20 transition-all duration-300 hover:bg-green-500 hover:shadow-green-500/30"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.05] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                    <KeyIcon className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </div>

              <div className="mb-8 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  Enter Verification Code
                </h1>
                <p className="text-[13px] text-slate-400">
                  We sent a code to {forgotEmail}
                </p>
              </div>

              <form onSubmit={handleResetSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="reset-code" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Verification Code
                  </Label>
                  <Input
                    id="reset-code"
                    type="text"
                    placeholder="123456"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-center text-[18px] font-mono tracking-[0.3em] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="reset-password" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    New Password
                  </Label>
                  <Input
                    id="reset-password"
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Confirm Password
                  </Label>
                  <Input
                    id="reset-confirm"
                    type="password"
                    value={resetConfirm}
                    onChange={(e) => setResetConfirm(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                <div className="rounded-lg border border-slate-700/30 bg-slate-800/20 px-4 py-3">
                  <p className="text-[12px] leading-relaxed text-slate-500">
                    Min 12 characters with uppercase, lowercase, numbers, and symbols.
                  </p>
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                    <p className="text-[13px] text-red-400">{error}</p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || !resetCode.trim() || !resetPassword}
                    className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Resetting...
                      </span>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </div>

                <p className="text-center text-[13px] text-slate-400">
                  <button
                    type="button"
                    onClick={() => { setForgotStep("none"); setResetCode(""); setResetPassword(""); setResetConfirm(""); setError(null); }}
                    className="font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    Back to login
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MFA setup screen — associate TOTP authenticator
  if (mfaSetup) {
    const otpauthUri = `otpauth://totp/prayingmantis:${encodeURIComponent(mfaSetup.email)}?secret=${mfaSetup.secretCode}&issuer=prayingmantis`;

    async function handleMfaSetupSubmit(e: React.FormEvent) {
      e.preventDefault();
      setError(null);
      setIsSubmitting(true);
      try {
        const result = await verifySoftwareToken(mfaSetup!.email, mfaSetup!.session, mfaCode);
        loginWithTokens(result.idToken, result.accessToken, result.refreshToken, result.expiresIn);
      } catch (err) {
        const code = (err as Record<string, unknown>).code as string;
        if (code === "EnableSoftwareTokenMFAException" || code === "CodeMismatchException") {
          setError("Invalid code. Please check your authenticator and try again.");
        } else if (code === "NotAuthorizedException") {
          setError("Session expired. Please sign in again.");
          setMfaSetup(null);
        } else {
          setError((err as Error).message || "Failed to verify MFA code");
        }
      } finally {
        setIsSubmitting(false);
      }
    }

    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-500/[0.05] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[440px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-violet-500/20 via-violet-500/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-violet-500/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-violet-500/20 bg-violet-500/[0.08]">
                    <ShieldIcon className="h-8 w-8 text-violet-400" />
                  </div>
                </div>
              </div>

              <div className="mb-6 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  Set up two-factor auth
                </h1>
                <p className="text-[13px] text-slate-400">
                  Scan the QR code with your authenticator app, then enter the 6-digit code
                </p>
              </div>

              <div className="mb-4 flex justify-center">
                <div className="rounded-lg border border-slate-700/30 bg-white p-3">
                  <QRCodeSVG value={otpauthUri} size={200} />
                </div>
              </div>

              <div className="mb-4 rounded-lg border border-slate-700/30 bg-slate-800/20 px-4 py-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  Manual entry key
                </p>
                <p className="break-all font-mono text-[13px] text-slate-300 select-all">
                  {mfaSetup.secretCode}
                </p>
              </div>

              <form onSubmit={handleMfaSetupSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-setup-code" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Verification Code
                  </Label>
                  <Input
                    id="mfa-setup-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-center text-[18px] font-mono tracking-[0.3em] text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:ring-violet-500/20"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                    <p className="text-[13px] text-red-400">{error}</p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || mfaCode.length !== 6}
                    className="w-full rounded-xl border-0 bg-violet-600 py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-violet-500/20 transition-all duration-300 hover:bg-violet-500 hover:shadow-violet-500/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Verifying...
                      </span>
                    ) : (
                      "Verify & Continue"
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MFA challenge screen — enter TOTP code
  if (mfaChallenge) {
    async function handleMfaChallengeSubmit(e: React.FormEvent) {
      e.preventDefault();
      setError(null);
      setIsSubmitting(true);
      try {
        const result = await respondToMfaChallenge(
          mfaChallenge!.email,
          mfaCode,
          mfaChallenge!.session,
        );
        loginWithTokens(result.idToken, result.accessToken, result.refreshToken, result.expiresIn);
      } catch (err) {
        const code = (err as Record<string, unknown>).code as string;
        if (code === "CodeMismatchException") {
          setError("Invalid code. Please try again.");
        } else if (code === "NotAuthorizedException") {
          setError("Session expired. Please sign in again.");
          setMfaChallenge(null);
          setMfaCode("");
        } else if (code === "TooManyRequestsException") {
          setError("Too many attempts. Please wait a moment and try again.");
        } else {
          setError((err as Error).message || "MFA verification failed");
        }
      } finally {
        setIsSubmitting(false);
      }
    }

    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
        <GridBg />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.06] blur-[120px]" />

        <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
            <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                    <LockIcon className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </div>

              <div className="mb-8 text-center">
                <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                  Two-factor authentication
                </h1>
                <p className="text-[13px] text-slate-400">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <form onSubmit={handleMfaChallengeSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mfa-code" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Authentication Code
                  </Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    required
                    disabled={isSubmitting}
                    className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-center text-[18px] font-mono tracking-[0.3em] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                  />
                </div>

                {error && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                    <p className="text-[13px] text-red-400">{error}</p>
                  </div>
                )}

                <div className="pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || mfaCode.length !== 6}
                    className="w-full rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2.5">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Verifying...
                      </span>
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>

                <p className="text-center text-[13px] text-slate-400">
                  <button
                    type="button"
                    onClick={() => { setMfaChallenge(null); setMfaCode(""); setError(null); }}
                    className="font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    Back to login
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main login screen
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background">
      <GridBg />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.06] blur-[120px]" />

      <div className={`relative z-10 w-full max-w-[400px] transition-all duration-700 ease-out ${mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="rounded-2xl bg-gradient-to-b from-primary/20 via-primary/5 to-transparent p-px">
          <div className="rounded-2xl bg-card/90 px-8 pb-8 pt-10 backdrop-blur-xl">
            {/* Shield */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-primary/10 blur-md" />
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-primary/20 bg-primary/[0.08]">
                  <img src="/logo.png" alt="" aria-hidden className="h-10 w-auto" />
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="mb-8 text-center">
              <h1 className="mb-1.5 text-[22px] font-semibold tracking-tight text-white">
                prayingmantis.cloud
              </h1>
              <p className="text-[13px] tracking-wide text-slate-400">
                AI-Powered Security Operations
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[12px] font-medium uppercase tracking-wider text-slate-400">
                    Password
                  </Label>
                  <button
                    type="button"
                    onClick={() => { setForgotStep("email"); setForgotEmail(email); setError(null); }}
                    className="text-[12px] font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    Forgot password?
                  </button>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="rounded-lg border-slate-700/50 bg-slate-800/30 py-5 text-[14px] text-white placeholder:text-slate-600 focus:border-primary/50 focus:ring-primary/20"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-4 py-3">
                  <p className="text-[13px] text-red-400">{error}</p>
                </div>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="group relative w-full overflow-hidden rounded-xl border-0 bg-primary py-6 text-[14px] font-medium tracking-wide text-white shadow-lg shadow-primary/20 transition-all duration-300 hover:bg-primary hover:shadow-primary/30 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2.5">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2.5">
                      <LockIcon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                      Sign In
                    </span>
                  )}
                </Button>
              </div>

              <p className="text-center text-[13px] text-slate-400">
                Don&apos;t have an account?{" "}
                <Link
                  to="/signup"
                  className="font-medium text-primary transition-colors hover:text-primary/80"
                >
                  Create one
                </Link>
              </p>
            </form>
          </div>
        </div>

        <div className="mx-auto mt-1 h-px w-3/4 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </div>

      <div className={`absolute bottom-6 text-center text-[11px] tracking-widest text-slate-600 transition-all delay-300 duration-700 ${mounted ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}>
        PRAYINGMANTIS.CLOUD
      </div>
    </div>
  );
}

function GridBg() {
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" opacity="0.6" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}
